import _ from 'lodash';

import {
  DocumentViewPreset,
  OhsDocumentFilterPayload,
  OhsDocumentSearchFilter,
  OhsDocumentSearchMain,
} from 'documents/models/OhsDocumentsModels';
import { OhsTabKeyTypes } from 'global-components/tab/OhsTabModels';
import { TierType } from 'global-services/constants/OhsObject';
import { OhsFormattedParamList, ParamTypes } from 'global-services/register/OhsRegisterModels';
import {
  getSortOptions,
  SortOptionKey,
  SortOptionsMap,
} from 'global-services/register/OhsSortModels';
import {
  formatListParamsToPayload,
  formatListPayloadToParams,
  normalizeSearchParams,
  resetFilter,
  returnHasFilter,
} from 'global-services/register/utils';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { getWorkplaceIds } from 'user/OhsUserServices';

interface Filter {
  sort: string;
  workplaces?: string[];
}

const sortOptions = getSortOptions([
  SortOptionKey.NameAToZ,
  SortOptionKey.NameZToA,
  SortOptionKey.DateCreatedNewestToOldest,
  SortOptionKey.DateCreatedOldestToNewest,
  SortOptionKey.DocumentTypeAToZ,
  SortOptionKey.DocumentTypeZToA,
  SortOptionKey.DateIssuedNewestToOldest,
  SortOptionKey.DateIssuedOldestToNewest,
  SortOptionKey.VersionHighestToLowest,
  SortOptionKey.VersionLowestToHighest,
]);

const sortSearchOptions = getSortOptions([
  SortOptionKey.Relevance,
  SortOptionKey.DateIssuedNewestToOldest,
  SortOptionKey.DateIssuedOldestToNewest,
  SortOptionKey.VersionHighestToLowest,
  SortOptionKey.VersionLowestToHighest,
]);

const templateSortOptions = getSortOptions([
  SortOptionKey.DateCreatedNewestToOldest,
  SortOptionKey.DateCreatedOldestToNewest,
]);

const templateSortSearchOptions = getSortOptions([
  SortOptionKey.DateIssuedNewestToOldest,
  SortOptionKey.DateIssuedOldestToNewest,
  SortOptionKey.VersionHighestToLowest,
  SortOptionKey.VersionLowestToHighest,
]);

const defaultDocumentFilter = (user: OhsUser) => {
  const getDocumentViewPreset = () => {
    if (user?.tier.type === TierType.T2) return DocumentViewPreset.View1;
    if (user?.tier.type === TierType.T3) return DocumentViewPreset.View2;
    return DocumentViewPreset.View4;
  };
  const workplaceOptions = getWorkplaceIds(user?.configs);

  return {
    [OhsTabKeyTypes.Documents]: {
      view1: {
        viewPreset: getDocumentViewPreset(),
        sort: SortOptionsMap.NameAToZ.value,
        categories: [],
      },
      view2: {
        viewPreset: getDocumentViewPreset(),
        sort: SortOptionsMap.NameAToZ.value,
        categories: [],
        reviewScheduled: false,
        allocatedToWorkplace: true,
        notAllocated: true,
      },
      view4: {
        viewPreset: getDocumentViewPreset(),
        sort: SortOptionsMap.NameAToZ.value,
        categories: [],
        reviewScheduled: false,
        allocatedToWorkplace: true,
        workplaceOwned: true,
      },
    },
    [OhsTabKeyTypes.Workplace]: {
      viewPreset: DocumentViewPreset.View3,
      sort: SortOptionsMap.NameAToZ.value,
      workplaces: workplaceOptions,
      categories: [],
    },
    [OhsTabKeyTypes.Template]: {
      viewPreset: DocumentViewPreset.View1,
      sort: SortOptionsMap.DateCreatedNewestToOldest.value,
      copied: true,
      copiedVersionUpdated: true,
      notCopied: true,
      categories: [],
    },
  };
};

const getDefaultDocumentFilter = (
  user: OhsUser,
  tab:
    | OhsTabKeyTypes.Documents
    | OhsTabKeyTypes.Workplace
    | OhsTabKeyTypes.Template = OhsTabKeyTypes.Documents
) => {
  // Workplace
  if (tab === OhsTabKeyTypes.Workplace) {
    return defaultDocumentFilter(user)[OhsTabKeyTypes.Workplace];
  }

  // Template
  if (tab === OhsTabKeyTypes.Template) {
    return defaultDocumentFilter(user)[OhsTabKeyTypes.Template];
  }

  // Documents
  switch (user.tierNum) {
    case 2:
      return defaultDocumentFilter(user)[OhsTabKeyTypes.Documents].view1;
    case 3:
      return defaultDocumentFilter(user)[OhsTabKeyTypes.Documents].view2;
    case 4:
    case 5:
      return defaultDocumentFilter(user)[OhsTabKeyTypes.Documents].view4;
    default:
      return defaultDocumentFilter(user)[OhsTabKeyTypes.Documents].view4;
  }
};

export default class OhsDocumentFilterModels {
  folderView: boolean;

  displayList: boolean;

  category: string;

  subcategory: string;

  archived: boolean;

  hasFilter: boolean;

  hasSearch: boolean;

  searchKey: string;

  page: number;

  tab: OhsTabKeyTypes.Documents | OhsTabKeyTypes.Workplace | OhsTabKeyTypes.Template;

  viewPreset: DocumentViewPreset;

  defaultFilter: Filter;

  filter: OhsDocumentFilterPayload;

  filterCount: OhsDocumentFilterPayload;

  searchFilter: OhsDocumentSearchMain;

  searchFilterCount: OhsDocumentSearchMain;

  buildFilters: (payload: number | any, next?: string) => any;

  buildResetFilter: () => any;

  categoryOptions: string[];

  subCategoryOptions: string[];

  sortOptions: { name: string; value: string }[];

  sortSearchOptions: { name: string; value: string }[];

  constructor({
    searchParams,
    overrides = {},
  }: {
    searchParams: URLSearchParams;
    overrides?: Partial<OhsFormattedParamList>;
  }) {
    const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;

    const rawCategoryOptions = user?.configs.vdoc?.CATEGORY_LIST ?? [];
    const categoryOptions = rawCategoryOptions.map((item) => item.category);

    const rawSubcategoryList = rawCategoryOptions.map((a: any) => a.subcategory);
    const subCategoryOptions = _.sortBy(
      _.uniqBy([...new Set(rawSubcategoryList.flat())], (item) => item)
    );

    const params = normalizeSearchParams(searchParams);
    const tab = (params?.tab || OhsTabKeyTypes.Documents) as
      | OhsTabKeyTypes.Documents
      | OhsTabKeyTypes.Workplace
      | OhsTabKeyTypes.Template;

    const defaultFilter = getDefaultDocumentFilter(user, tab);
    const { viewPreset } = defaultFilter;

    const rawFormattedParams = formatListParamsToPayload(searchParams, {
      defaultFilter,
      categoryOptions,
      subCategoryOptions,
    });
    const formattedParams = { ...rawFormattedParams, ...overrides };

    const {
      folderView,
      category,
      subcategory,
      archived,
      sort,
      workplaces,
      dateIssued,
      reviewScheduled,
      allocatedToWorkplace,
      notAllocated,
      copied,
      copiedVersionUpdated,
      notCopied,
      categories,
      subcategories,
      workplaceOwned,

      hasSearch,
      searchKey,
      substringSearch,

      page,
      next,
    } = formattedParams;

    const baseFilter: Partial<OhsDocumentFilterPayload> = {
      archived,
      categories,
      subcategories,
      ...(next && page > 10 ? { next } : { page }),
      ...(dateIssued.start && dateIssued.end && { dateIssued }), // conditional inclusion
    };

    const allFilters: Partial<Record<OhsTabKeyTypes, Partial<OhsDocumentFilterPayload>>> = {
      [OhsTabKeyTypes.Documents]: {
        ...baseFilter,
        reviewScheduled,
        allocatedToWorkplace,
        ...(user.tierNum === 3 ? { notAllocated } : {}),
        ...(user.tierNum === 4 || user.tierNum === 5 ? { workplaceOwned } : {}),
      },
      [OhsTabKeyTypes.Workplace]: {
        ...baseFilter,
        workplaces,
      },
      [OhsTabKeyTypes.Template]: {
        ...baseFilter,
        copied,
        copiedVersionUpdated,
        notCopied,
      },
    };

    const allSearchFilters: Partial<Record<OhsTabKeyTypes, Partial<OhsDocumentSearchFilter>>> = {
      [OhsTabKeyTypes.Documents]: { ...baseFilter },
      [OhsTabKeyTypes.Workplace]: { ...baseFilter, workplaces },
      [OhsTabKeyTypes.Template]: { ...baseFilter },
    };

    // ============ Params ============
    this.archived = archived;
    this.folderView = folderView;
    this.displayList = !folderView || !!(folderView && category && subcategory);
    this.category = category;
    this.subcategory = subcategory;
    this.hasSearch = hasSearch;
    this.searchKey = searchKey;
    this.page = page;
    this.tab = tab || OhsTabKeyTypes.Documents;
    this.viewPreset = viewPreset;

    // ============ Options ============
    this.categoryOptions = categoryOptions;
    this.subCategoryOptions = subCategoryOptions;
    this.sortOptions = tab === OhsTabKeyTypes.Template ? templateSortOptions : sortOptions;
    this.sortSearchOptions =
      tab === OhsTabKeyTypes.Template ? templateSortSearchOptions : sortSearchOptions;

    // ============ Filter Objs ============
    const validSort = this.sortOptions.find((item) => item.value === JSON.stringify(sort));
    const filter: OhsDocumentFilterPayload = {
      ...allFilters[tab],
      sort: validSort ? sort : JSON.parse(this.sortOptions[0].value),
      viewPreset,
      count: false,
    };

    const validSearchSort = this.sortSearchOptions.find(
      (item) => item.value === JSON.stringify(sort)
    );
    const searchFilter: OhsDocumentSearchMain = {
      searchKey,
      substringSearch,
      highlight: false,
      viewPreset,
      sort: validSearchSort ? sort : JSON.parse(this.sortSearchOptions[0].value),
      filter: {
        ...(allSearchFilters[tab] as OhsDocumentSearchFilter),
      },
    };

    this.defaultFilter = defaultFilter;

    this.filter = filter;

    this.filterCount = { ...filter, count: true };

    this.searchFilter = searchFilter;

    this.searchFilterCount = {
      ...searchFilter,
      filter: {
        ...searchFilter.filter,
        count: true,
      },
    };

    if (dateIssued.start && dateIssued.end) {
      this.searchFilter.filter.dateIssued = dateIssued;
      this.searchFilterCount.filter.dateIssued = dateIssued;
    }

    // on folder view, selected category and subcategory should not affect filter status
    const conditionalDefaultFilter = folderView
      ? {
          ...defaultFilter,
          category,
          subcategory,
          categories,
          subcategories,
        }
      : defaultFilter;
    this.hasFilter = returnHasFilter(conditionalDefaultFilter, this.filter);

    // ============ Funcs ===========
    this.buildFilters = (payload: number | any, nextId?: string) => {
      let newPayload: any = {};

      if (typeof payload === 'number') {
        if (hasSearch) {
          newPayload = this.searchFilter;
          newPayload.filter[ParamTypes.Page] = payload;
        } else {
          newPayload = this.filter;
          newPayload[ParamTypes.Page] = payload;
        }
        if (payload > 10) newPayload[ParamTypes.Next] = nextId;
      } else {
        newPayload = {
          ...payload,
          [ParamTypes.Page]: 1,
          [ParamTypes.Next]: undefined,
        };
      }

      const newParams = formatListPayloadToParams({ ...newPayload, tab }, defaultFilter, {
        withWorkplace: true,
        categoryOptions,
        subCategoryOptions,
        sortOptions: {
          filter: this.sortOptions,
          search: this.sortSearchOptions,
        },
      });
      return new URLSearchParams(newParams);
    };

    this.buildResetFilter = () => {
      return resetFilter(searchParams);
    };
  }
}
